/**
 * The single stylesheet injected into the shadow root. Tokens are CSS custom properties so the
 * backend (via `theme.vars`) and host (via dark mode) can retheme without touching markup.
 * Animations use only `transform`/`opacity`; `:host([data-motion='reduced'])` disables them.
 */
export const BASE_CSS = /* css */ `
:host {
  all: initial;
  --cr-accent: #0aa3a3;
  --cr-accent-contrast: #ffffff;
  --cr-bg: #ffffff;
  --cr-surface: #ffffff;
  --cr-fg: #1c2024;
  --cr-muted: #6b7280;
  --cr-border: #e7e9ec;
  --cr-overlay: rgba(17, 24, 28, 0.45);
  --cr-dot: #ef4444;
  --cr-type-feature: #16a34a;
  --cr-type-improvement: #2563eb;
  --cr-type-fix: #d97706;
  --cr-radius: 14px;
  --cr-radius-control: 8px;
  --cr-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  --cr-z: 2147483000;
  --cr-anim: 240ms cubic-bezier(0.22, 1, 0.36, 1);
  --cr-font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

:host([data-theme='dark']) {
  --cr-bg: #15181b;
  --cr-surface: #1b1f23;
  --cr-fg: #e7eaed;
  --cr-muted: #9aa3ad;
  --cr-border: #2a2f35;
  --cr-overlay: rgba(0, 0, 0, 0.6);
  --cr-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  --cr-type-feature: #4ade80;
  --cr-type-improvement: #60a5fa;
  --cr-type-fix: #fbbf24;
}
@media (prefers-color-scheme: dark) {
  :host([data-theme='auto']) {
    --cr-bg: #15181b;
    --cr-surface: #1b1f23;
    --cr-fg: #e7eaed;
    --cr-muted: #9aa3ad;
    --cr-border: #2a2f35;
    --cr-overlay: rgba(0, 0, 0, 0.6);
    --cr-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    --cr-type-feature: #4ade80;
    --cr-type-improvement: #60a5fa;
    --cr-type-fix: #fbbf24;
  }
}

:host([data-motion='reduced']) * {
  transition: none !important;
  animation: none !important;
}

.cr-overlay,
.cr-banner,
.cr-toast,
.cr-badge {
  box-sizing: border-box;
  font-family: var(--cr-font);
}
.cr-overlay *,
.cr-banner *,
.cr-toast * {
  box-sizing: border-box;
}

/* ---- Changelog overlay (modal + sidebar) ---- */
.cr-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--cr-z);
}
.cr-overlay__backdrop {
  position: absolute;
  inset: 0;
  background: var(--cr-overlay);
  opacity: 0;
  transition: opacity var(--cr-anim);
}
.cr-overlay.is-open .cr-overlay__backdrop {
  opacity: 1;
}
.cr-overlay__positioner {
  position: absolute;
  inset: 0;
  display: flex;
}
.cr-overlay--modal .cr-overlay__positioner {
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.cr-overlay--sidebar .cr-overlay__positioner {
  align-items: stretch;
  justify-content: flex-end;
}
.cr-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--cr-surface);
  color: var(--cr-fg);
  box-shadow: var(--cr-shadow);
  overflow: hidden;
}
.cr-overlay--modal .cr-panel {
  width: min(680px, 100%);
  max-height: min(80vh, 720px);
  border-radius: var(--cr-radius);
  opacity: 0;
  transform: translateY(12px) scale(0.985);
  transition: transform var(--cr-anim), opacity var(--cr-anim);
}
.cr-overlay--modal.is-open .cr-panel {
  opacity: 1;
  transform: none;
}
.cr-overlay--sidebar .cr-panel {
  width: min(420px, 100%);
  height: 100%;
  transform: translateX(100%);
  transition: transform var(--cr-anim);
}
.cr-overlay--sidebar.is-open .cr-panel {
  transform: none;
}

.cr-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--cr-border);
}
.cr-panel__title {
  margin: 0;
  font: 600 16px/1.25 var(--cr-font);
}
.cr-panel__close {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--cr-muted);
  font-size: 22px;
  line-height: 1;
  width: 32px;
  height: 32px;
  border-radius: var(--cr-radius-control);
  cursor: pointer;
  transition: background var(--cr-anim), color var(--cr-anim);
}
.cr-panel__close:hover {
  background: var(--cr-border);
  color: var(--cr-fg);
}
.cr-panel__body {
  overflow: auto;
  padding: 4px 18px 18px;
  -webkit-overflow-scrolling: touch;
}

/* ---- Changelog entries ---- */
.cr-changelog {
  display: flex;
  flex-direction: column;
}
.cr-entry {
  padding: 16px 0;
  border-bottom: 1px solid var(--cr-border);
}
.cr-entry:last-child {
  border-bottom: 0;
}
.cr-entry__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.cr-entry__title {
  margin: 0;
  font: 600 15px/1.35 var(--cr-font);
}
.cr-entry__date {
  flex: none;
  color: var(--cr-muted);
  font: 500 12px/1 var(--cr-font);
  white-space: nowrap;
}
.cr-entry__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 0;
}
.cr-tag {
  background: rgba(10, 163, 163, 0.14);
  background: color-mix(in srgb, var(--cr-accent) 14%, transparent);
  color: var(--cr-accent);
  font: 600 11px/1 var(--cr-font);
  padding: 4px 8px;
  border-radius: 999px;
}
.cr-entry__type {
  margin-top: 8px;
}
.cr-type {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 600 11px/1 var(--cr-font);
  padding: 4px 9px;
  border-radius: 999px;
  color: var(--cr-muted);
  background: color-mix(in srgb, var(--cr-muted) 15%, transparent);
}
.cr-type::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
}
.cr-type--feature {
  color: var(--cr-type-feature);
  background: color-mix(in srgb, var(--cr-type-feature) 15%, transparent);
}
.cr-type--improvement {
  color: var(--cr-type-improvement);
  background: color-mix(in srgb, var(--cr-type-improvement) 15%, transparent);
}
.cr-type--fix {
  color: var(--cr-type-fix);
  background: color-mix(in srgb, var(--cr-type-fix) 16%, transparent);
}
.cr-entry__body {
  margin-top: 8px;
  font: 400 14px/1.55 var(--cr-font);
  color: var(--cr-fg);
}
.cr-entry__body :is(h1, h2, h3, h4) {
  font-size: 14px;
  margin: 12px 0 6px;
}
.cr-entry__body p {
  margin: 6px 0;
}
.cr-entry__body ul,
.cr-entry__body ol {
  margin: 6px 0;
  padding-left: 18px;
}
.cr-entry__body a {
  color: var(--cr-accent);
}
.cr-entry__more {
  appearance: none;
  margin-top: 10px;
  border: 1px solid var(--cr-border);
  background: transparent;
  color: var(--cr-accent);
  font: 600 12px/1 var(--cr-font);
  padding: 7px 12px;
  border-radius: var(--cr-radius-control);
  cursor: pointer;
  transition: background var(--cr-anim);
}
.cr-entry__more:hover:not(:disabled) {
  background: color-mix(in srgb, var(--cr-accent) 10%, transparent);
}
.cr-entry__more:disabled {
  cursor: default;
  opacity: 0.7;
}
.cr-entry__more-error {
  margin-left: 8px;
  color: var(--cr-muted);
  font: 500 12px/1.4 var(--cr-font);
}
.cr-changelog__footer {
  display: flex;
  padding: 16px 0 0;
}
.cr-changelog__more-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  border: 1px solid var(--cr-accent);
  background: var(--cr-accent);
  color: var(--cr-accent-contrast);
  font: 600 13px/1 var(--cr-font);
  padding: 9px 14px;
  border-radius: var(--cr-radius-control);
  text-decoration: none;
  cursor: pointer;
  transition: filter var(--cr-anim), box-shadow var(--cr-anim);
}
.cr-changelog__more-link:hover {
  filter: brightness(0.96);
}
.cr-changelog__more-link:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--cr-accent) 45%, transparent);
  outline-offset: 2px;
}
.cr-empty {
  color: var(--cr-muted);
  padding: 28px 0;
  text-align: center;
}

/* ---- Banner ---- */
.cr-banner {
  position: fixed;
  left: 0;
  right: 0;
  z-index: var(--cr-z);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: var(--cr-banner-bg, var(--cr-accent));
  color: var(--cr-banner-fg, var(--cr-accent-contrast));
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.14);
}
.cr-banner--top {
  top: 0;
  transform: translateY(-100%);
  transition: transform var(--cr-anim);
}
.cr-banner--bottom {
  bottom: 0;
  transform: translateY(100%);
  transition: transform var(--cr-anim);
}
.cr-banner.is-open {
  transform: none;
}
.cr-banner__text {
  flex: 1 1 auto;
  min-width: 0;
  font: 500 14px/1.4 var(--cr-font);
}
.cr-banner__title {
  font-weight: 600;
}
.cr-banner__cta {
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: transparent;
  color: inherit;
  font: 600 13px/1 var(--cr-font);
  padding: 7px 12px;
  border-radius: var(--cr-radius-control);
  cursor: pointer;
  white-space: nowrap;
}
.cr-banner__cta:hover {
  background: rgba(255, 255, 255, 0.15);
}
.cr-banner__close {
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  opacity: 0.85;
  font-size: 20px;
  line-height: 1;
  width: 28px;
  height: 28px;
  border-radius: var(--cr-radius-control);
  cursor: pointer;
}
.cr-banner__close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.15);
}

/* ---- Toast ---- */
.cr-toast {
  position: fixed;
  z-index: var(--cr-z);
  width: min(360px, calc(100vw - 32px));
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 14px 14px 16px;
  background: var(--cr-surface);
  color: var(--cr-fg);
  border: 1px solid var(--cr-border);
  border-radius: var(--cr-radius);
  box-shadow: var(--cr-shadow);
  opacity: 0;
  transition: transform var(--cr-anim), opacity var(--cr-anim);
}
.cr-toast--top-right {
  top: 16px;
  right: 16px;
  transform: translateY(-12px);
}
.cr-toast--bottom-right {
  bottom: 16px;
  right: 16px;
  transform: translateY(12px);
}
.cr-toast--top-left {
  top: 16px;
  left: 16px;
  transform: translateY(-12px);
}
.cr-toast--bottom-left {
  bottom: 16px;
  left: 16px;
  transform: translateY(12px);
}
.cr-toast.is-open {
  opacity: 1;
  transform: none;
}
.cr-toast__main {
  flex: 1 1 auto;
  min-width: 0;
}
.cr-toast__main > .cr-tag {
  display: inline-block;
  margin-bottom: 6px;
}
.cr-toast__title {
  font: 600 14px/1.4 var(--cr-font);
}
.cr-toast__body {
  margin-top: 4px;
  color: var(--cr-muted);
  font: 400 13px/1.5 var(--cr-font);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.cr-toast__actions {
  margin-top: 10px;
}
.cr-toast__cta {
  appearance: none;
  border: 0;
  background: var(--cr-accent);
  color: var(--cr-accent-contrast);
  font: 600 12px/1 var(--cr-font);
  padding: 7px 12px;
  border-radius: var(--cr-radius-control);
  cursor: pointer;
}
.cr-toast__cta:hover {
  filter: brightness(0.96);
}
.cr-toast__close {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--cr-muted);
  font-size: 18px;
  line-height: 1;
  width: 24px;
  height: 24px;
  border-radius: var(--cr-radius-control);
  cursor: pointer;
}
.cr-toast__close:hover {
  background: var(--cr-border);
  color: var(--cr-fg);
}

/* ---- Indicator badge (overlaid on a host element, never mutates host DOM) ---- */
.cr-badge {
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--cr-z);
  min-width: 10px;
  height: 10px;
  background: var(--cr-dot);
  border-radius: 999px;
  box-shadow: 0 0 0 2px var(--cr-bg);
  pointer-events: none;
  transform: scale(0);
  transform-origin: center;
  transition: transform var(--cr-anim);
}
.cr-badge.is-visible {
  transform: scale(1);
}
.cr-badge--count {
  height: 18px;
  min-width: 18px;
  padding: 0 5px;
  color: #fff;
  font: 600 11px/18px var(--cr-font);
  text-align: center;
}
`;
